import { formatPrice } from '@/utils'

/**
 * @description 买赠赠送条件文案（discountCalc=2 每满显示「每满」，阶梯/满赠显示「满」）
 * @param {{ buyNum?: number, discountCalc?: number }} params
 * @returns {string}
 */
export function resolveBuyGiftConditionText({ buyNum, discountCalc, unit = '台' } = {}) {
  const num = Number(buyNum)
  const safeBuyNum = Number.isFinite(num) && num > 0 ? num : 1
  const prefix = Number(discountCalc) === 2 ? '每满' : '满'
  const u = String(unit || '台').trim() || '台'
  return `${prefix}${safeBuyNum}${u}享以下赠品，多买多送，赠完为止`
}

/**
 * @description 买赠赠品价值展示文案（仅依据 tier.giftTotalValue，含 0 元）
 * @param {number|string|null|undefined} giftTotalValue
 * @returns {string}
 */
export function resolveBuyGiftValueText(giftTotalValue) {
  const value = Number(giftTotalValue)
  if (!Number.isFinite(value) || value < 0) return ''
  return formatPrice(value)
}

/**
 * @description 买赠阶梯按 buyNum 升序
 * @param {Array<Object>} tiers
 * @returns {Array<Object>}
 */
export function sortBuyGiftTiers(tiers = []) {
  if (!Array.isArray(tiers) || tiers.length === 0) return []
  return [...tiers].sort((a, b) => Number(a.buyNum) - Number(b.buyNum))
}

/**
 * @description 从对象取价目表单价（不含活动价 promotionPrice / displayPrice）
 * @param {Object|null|undefined} source
 * @param {string[]} [keys]
 * @returns {number|null}
 */
export function pickGiftCatalogUnitPrice(source, keys = []) {
  if (!source || typeof source !== 'object') return null
  const priceKeys = [
    ...keys,
    'originalPrice',
    'originalUnitPrice',
    'unitPrice',
    'listPrice',
    'catalogPrice',
    'oriPrice',
    'smbPrice',
    'terminalPrice',
  ]
  for (const key of priceKeys) {
    const value = Number(source[key])
    if (Number.isFinite(value) && value >= 0) return value
  }
  return null
}

/**
 * @description 阶梯赠品售价之和（按赠品价目表单价×数量汇总，回退接口 giftTotalValue）
 * @param {Object|null|undefined} tier
 * @returns {number|null}
 */
export function resolveBuyGiftTierGiftTotalValue(tier) {
  if (!tier || typeof tier !== 'object') return null

  const gifts = Array.isArray(tier.gifts) ? tier.gifts : []
  if (gifts.length) {
    let sum = 0
    let hasValue = false

    gifts.forEach((gift) => {
      const unitPrice = pickGiftCatalogUnitPrice(gift)
      const qty = Number(gift?.giveawayNum ?? 1)
      if (unitPrice !== null && Number.isFinite(qty) && qty > 0) {
        sum += unitPrice * qty
        hasValue = true
      }
    })

    if (hasValue) return sum
  }

  const direct = Number(tier.giftTotalValue)
  if (Number.isFinite(direct) && direct >= 0) return direct

  return null
}

/**
 * @description 解析买赠阶梯门槛数量
 * @param {Object|null|undefined} tier
 * @returns {number|null}
 */
export function resolveBuyGiftTierBuyNum(tier) {
  const buyNum = Number(tier?.buyNum ?? tier?.threshold)
  return Number.isFinite(buyNum) && buyNum > 0 ? buyNum : null
}

/**
 * @description 无阶梯买赠卡片展示金额（阶梯满赠：赠品售价之和/门槛；每满循环：赠品售价之和）
 * @param {number|string} giftTotalValue
 * @param {number|string} buyNum
 * @returns {number|null}
 */
export function calcBuyGiftSingleTierSaveAmount(giftTotalValue, buyNum) {
  const total = Number(giftTotalValue)
  const threshold = Number(buyNum)
  if (!Number.isFinite(total) || total < 0) return null
  if (!Number.isFinite(threshold) || threshold <= 0) return null
  return total / threshold
}

/**
 * @description 买赠/活动卡片节省金额展示（去掉无意义 .00，保留有效小数）
 * @param {number|string} value
 * @returns {string}
 */
export function formatPromotionSaveAmountText(value) {
  return formatBuyGiftSaveAmount(value)
}

/**
 * @description 无阶梯买赠价格后缀：/ M单位（如 / 2台）
 * @param {number|string} buyNum
 * @param {string} [unit='台']
 * @returns {string}
 */
export function formatBuyGiftThresholdSuffix(buyNum, unit = '台') {
  const num = Number(buyNum)
  if (!Number.isFinite(num) || num <= 0) return ''
  const qty = Number.isInteger(num)
    ? String(num)
    : String(Number(num.toFixed(2))).replace(/\.?0+$/, '')
  const safeUnit = String(unit || '台').trim() || '台'
  return ` / ${qty}${safeUnit}`
}

/**
 * @description 买赠最高阶梯（buyNum 最大档）
 * @param {Array<Object>} tiers
 * @returns {Object|null}
 */
export function getHighestBuyGiftTier(tiers = []) {
  const sorted = sortBuyGiftTiers(tiers)
  if (!sorted.length) return null
  return sorted[sorted.length - 1]
}

/**
 * @description 活动商品行阶梯/标签文案列表（优先 labels，其次 tiers.displayText）
 * @param {Object|null|undefined} productRow
 * @returns {string[]}
 */
export function resolveBuyGiftPromoTagTexts(productRow) {
  const labels = Array.isArray(productRow?.labels) ? productRow.labels : []
  if (labels.length) {
    return labels.map((item) => String(item?.displayText || '').trim()).filter(Boolean)
  }

  const tiers = sortBuyGiftTiers(productRow?.tiers)
  return tiers.map((tier) => String(tier?.displayText || '').trim()).filter(Boolean)
}

/**
 * @description 映射 API 赠品项为商详/弹层通用结构
 * @param {Object} gift
 * @returns {Object}
 */
export function mapBuyGiftTierGiftItem(gift) {
  if (!gift || typeof gift !== 'object') return null
  return {
    prodId: gift.prodId,
    prodName: gift.prodName,
    name: gift.prodName,
    modelName: gift.prodModel,
    pic: gift.prodImage,
    prodImage: gift.prodImage,
    giveawayNum: gift.giveawayNum,
    price: gift.unitPrice,
    oriPrice: gift.unitPrice,
    totalValue: gift.totalValue,
  }
}

/**
 * @description 买赠阶梯映射为商详弹层 groups
 * @param {Array<Object>} tiers
 * @param {{ discountCalc?: number }} [options]
 * @returns {Array<Object>}
 */
export function mapBuyGiftTiersToRuleGroups(tiers = [], options = {}) {
  const discountCalc = Number(options.discountCalc)

  return sortBuyGiftTiers(tiers)
    .map((tier, index) => {
      const gifts = (Array.isArray(tier?.gifts) ? tier.gifts : [])
        .map(mapBuyGiftTierGiftItem)
        .filter(Boolean)
      if (!gifts.length) return null

      const buyNum = Number(tier?.buyNum)
      const safeBuyNum = Number.isFinite(buyNum) && buyNum > 0 ? buyNum : 1
      const giftTotalValue = Number(tier?.giftTotalValue)
      const safeGiftTotalValue =
        Number.isFinite(giftTotalValue) && giftTotalValue >= 0 ? giftTotalValue : 0

      return {
        key: tier?.hierarchyCode || `${safeBuyNum}-${index}`,
        buyNum: safeBuyNum,
        displayText: String(tier?.displayText || '').trim(),
        conditionText: resolveBuyGiftConditionText({ buyNum: safeBuyNum, discountCalc }),
        giftTotalValue: safeGiftTotalValue,
        value: safeGiftTotalValue,
        valueText: resolveBuyGiftValueText(tier?.giftTotalValue),
        gifts,
      }
    })
    .filter(Boolean)
}

/**
 * @description 买赠节省金额展示（去掉无意义 .00）
 * @param {number|string} value
 * @returns {string}
 */
function formatBuyGiftSaveAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num) || num < 0) return ''
  const formatted = formatPrice(num)
  const dotIndex = formatted.lastIndexOf('.')
  if (dotIndex === -1) return formatted
  const integerPart = formatted.slice(0, dotIndex)
  const decimalPart = formatted.slice(dotIndex + 1).replace(/0+$/, '')
  return decimalPart ? `${integerPart}.${decimalPart}` : integerPart
}

/**
 * @description 将接口买赠标签规范为「满N台省¥X」
 * @param {string} text
 * @returns {string}
 */
export function normalizeBuyGiftTierPriceTagLabel(text) {
  let normalized = String(text || '').trim()
  if (!normalized) return ''

  normalized = normalized.replace(/件/g, '台').replace(/¥\s+/g, '¥')

  const saveMatch = normalized.match(/^(每满|满)(\d+(?:\.\d+)?)台省[¥￥]?([\d,.]+)(?:元)?$/)
  if (saveMatch) {
    const amount = formatBuyGiftSaveAmount(String(saveMatch[3]).replace(/,/g, ''))
    if (amount) return `${saveMatch[1]}${saveMatch[2]}台省¥${amount}`
  }

  const giftMatch = normalized.match(/^(每满|满)(\d+(?:\.\d+)?)台享赠品$/)
  if (giftMatch) return ''

  const legacyGiftValueMatch = normalized.match(
    /^(每满|满)(\d+(?:\.\d+)?)[台件]享赠品\s*(?:[（(]\s*价值\s*([\d,.]+)\s*[）)]|价值\s*([\d,.]+))$/
  )
  if (legacyGiftValueMatch) {
    const amount = formatBuyGiftSaveAmount(
      String(legacyGiftValueMatch[3] || legacyGiftValueMatch[4] || '').replace(/,/g, '')
    )
    if (amount) return `${legacyGiftValueMatch[1]}${legacyGiftValueMatch[2]}台省¥${amount}`
  }

  return normalized
}

/**
 * @description 根据买赠阶梯生成活动商品卡标签
 * @param {Object|string} tier
 * @param {number} [discountCalc]
 * @returns {string}
 */
export function resolveBuyGiftTierPriceTagLabel(tier, discountCalc) {
  if (typeof tier === 'string') return normalizeBuyGiftTierPriceTagLabel(tier)
  if (!tier || typeof tier !== 'object') return ''

  const calc = tier.discountCalc ?? discountCalc
  const buyNum = Number(tier.buyNum ?? tier.threshold)
  const giftTotalValue = resolveBuyGiftTierGiftTotalValue(tier)
  const amount =
    giftTotalValue !== null
      ? formatBuyGiftSaveAmount(giftTotalValue)
      : formatBuyGiftSaveAmount(tier.saveAmount ?? tier.totalValue ?? tier.discount)

  if (Number.isFinite(buyNum) && buyNum > 0 && amount !== '') {
    const prefix = Number(calc) === 2 ? '每满' : '满'
    const qty = Number.isInteger(buyNum) ? String(buyNum) : String(buyNum)
    return `${prefix}${qty}台省¥${amount}`
  }

  const displayText = tier.displayText ?? tier.label ?? tier.text
  return displayText ? normalizeBuyGiftTierPriceTagLabel(displayText) : ''
}

/**
 * @description 活动落地页买赠阶梯标签：每满→立省¥M/N单位；阶梯满赠→满N单位省¥M
 * @param {Object|null|undefined} tier
 * @param {{ discountCalc?: number, unit?: string }} [options]
 * @returns {string}
 */
export function resolveBuyGiftLandingPromoTagLabel(tier, options = {}) {
  if (!tier || typeof tier !== 'object') return ''

  const buyNum = resolveBuyGiftTierBuyNum(tier)
  if (!buyNum) return ''

  const giftTotalValue = resolveBuyGiftTierGiftTotalValue(tier)
  if (giftTotalValue === null) return ''

  const amount = formatBuyGiftSaveAmount(giftTotalValue)
  if (amount === '') return ''

  const calc = tier.discountCalc ?? options.discountCalc
  const safeUnit = String(options.unit || tier.prodUnit || '台').trim() || '台'
  const qty = formatBuyGiftTierQuantity(buyNum)
  if (!qty) return ''

  if (Number(calc) === 2) {
    if (buyNum === 1) {
      return `立省¥${amount}/${safeUnit}`
    }
    return `立省¥${amount}/${qty}${safeUnit}`
  }

  return `满${qty}${safeUnit}省¥${amount}`
}

/**
 * @description 活动列表阶梯标签数据源（labels 优先，回退 tiers）
 * @param {Object|null|undefined} productRow
 * @returns {Array<{ label: string, threshold?: number, price?: number }>}
 */
export function mapBuyGiftTierPriceTags(productRow) {
  const discountCalc = productRow?.discountCalc
  const unit = productRow?.prodUnit ?? '台'
  const tagOptions = { discountCalc, unit }

  const labels = Array.isArray(productRow?.labels) ? productRow.labels : []
  if (labels.length) {
    return labels
      .map((item) => {
        const label = resolveBuyGiftLandingPromoTagLabel(item, tagOptions)
        if (!label) return null
        return {
          label,
          threshold: item?.buyNum,
        }
      })
      .filter(Boolean)
  }

  return sortBuyGiftTiers(productRow?.tiers)
    .map((tier) => {
      const label = resolveBuyGiftLandingPromoTagLabel(tier, tagOptions)
      if (!label) return null
      return {
        label,
        threshold: tier.buyNum,
      }
    })
    .filter(Boolean)
}

function formatBuyGiftTierQuantity(value) {
  const num = Number(value)
  if (!Number.isFinite(num) || num <= 0) return ''
  if (Number.isInteger(num)) return String(num)
  return String(Number(num.toFixed(2))).replace(/\.?0+$/, '')
}

/**
 * @description 商详买赠单阶梯标签：满/每满 N{单位}享¥M赠品
 * @param {Object|null|undefined} tier
 * @param {{ discountCalc?: number, unit?: string }} [options]
 * @returns {string}
 */
export function resolveDetailBuyGiftPromoTagLabel(tier, options = {}) {
  if (!tier || typeof tier !== 'object') return ''

  const buyNum = resolveBuyGiftTierBuyNum(tier)
  if (!buyNum) return ''

  const giftTotalValue = resolveBuyGiftTierGiftTotalValue(tier)
  if (giftTotalValue === null) return ''

  const amount = formatBuyGiftSaveAmount(giftTotalValue)
  if (amount === '') return ''

  const calc = tier.discountCalc ?? options.discountCalc
  const prefix = Number(calc) === 2 ? '每满' : '满'
  const safeUnit = String(options.unit || tier.prodUnit || '台').trim() || '台'
  const qty = formatBuyGiftTierQuantity(buyNum)
  if (!qty) return ''

  return `${prefix}${qty}${safeUnit}享¥${amount}赠品`
}

/**
 * @description 商详买赠阶梯标签（满/每满 N单位享¥M赠品；M=赠品价目表售价×赠送数量之和）
 * @param {Object|null|undefined} info
 * @param {{ prodUnit?: string, discountCalc?: number }} [options]
 * @returns {string[]}
 */
export function buildDetailBuyGiftPromoTags(info, options = {}) {
  if (!info || typeof info !== 'object') return []

  const discountCalc = info.discountCalc ?? options.discountCalc
  const unit = info.prodUnit ?? options.prodUnit ?? '台'
  const tagOptions = { discountCalc, unit }

  const tiers = sortBuyGiftTiers(info.tiers)
  if (tiers.length) {
    const labels = tiers
      .map((tier) => resolveDetailBuyGiftPromoTagLabel(tier, tagOptions))
      .filter(Boolean)
    if (labels.length) return labels
  }

  const ruleSources = [info.giveawayRules, info.rules, info.ruleList, info.levels]
  for (const rules of ruleSources) {
    if (!Array.isArray(rules) || !rules.length) continue
    const labels = rules
      .map((rule) =>
        resolveDetailBuyGiftPromoTagLabel(
          {
            buyNum: rule?.buyNum,
            giftTotalValue: rule?.giftTotalValue ?? rule?.value,
            gifts: rule?.gifts,
            discountCalc,
          },
          tagOptions
        )
      )
      .filter(Boolean)
    if (labels.length) return labels
  }

  const label = resolveDetailBuyGiftPromoTagLabel(
    {
      buyNum: info.buyNum,
      giftTotalValue: info.giftTotalValue,
      gifts: info.giveawayProds ?? info.gifts,
      discountCalc,
    },
    tagOptions
  )
  return label ? [label] : []
}
